<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import ImageWithFallback from './figma/ImageWithFallback.vue';

const { t } = useI18n();

const carouselSlides = computed(() => [
  {
    image:
      'https://images.unsplash.com/photo-1739298061740-5ed03045b280?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx0ZWFtJTIwY29sbGFib3JhdGlvbiUyMHdvcmtzcGFjZXxlbnwxfHx8fDE3Njk0MzQyMTF8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    tagline: t('carousel.slide_1_tagline'),
    description: t('carousel.slide_1_description'),
  },
  {
    image:
      'https://images.unsplash.com/photo-1763191213523-1489179a1088?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjcmVhdGl2ZSUyMG9mZmljZSUyMG1vZGVybnxlbnwxfHx8fDE3NjkzNTkzMTd8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    tagline: t('carousel.slide_2_tagline'),
    description: t('carousel.slide_2_description'),
  },
  {
    image:
      'https://images.unsplash.com/photo-1758599543139-a80cf330df35?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9kdWN0aXZpdHklMjBzdWNjZXNzJTIwbGlmZXN0eWxlfGVufDF8fHx8MTc2OTQ2MDA1OHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    tagline: t('carousel.slide_3_tagline'),
    description: t('carousel.slide_3_description'),
  },
]);

const currentSlide = ref(0);
let timer: number | undefined;

onMounted(() => {
  timer = window.setInterval(() => {
    currentSlide.value = (currentSlide.value + 1) % carouselSlides.value.length;
  }, 5000);
});

onUnmounted(() => {
  if (timer !== undefined) {
    clearInterval(timer);
  }
});
</script>

<template>
  <div class="relative w-full h-full flex flex-col p-16 justify-center">
    <!-- Content -->
    <div class="relative z-10 mb-12">
      <Transition
        mode="out-in"
        enter-active-class="transition-all duration-400"
        enter-from-class="opacity-0 translate-y-2"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition-all duration-400"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-2"
      >
        <div :key="currentSlide">
          <h2 class="text-3xl text-[#F5F0EA] mb-3">
            {{ carouselSlides[currentSlide].tagline }}
          </h2>
          <p class="text-lg text-[#9E9080]">
            {{ carouselSlides[currentSlide].description }}
          </p>
        </div>
      </Transition>
    </div>

    <!-- Image Container -->
    <div class="flex-1 relative rounded-3xl overflow-hidden bg-[#1E1E23] max-h-96">
      <Transition
        mode="out-in"
        enter-active-class="transition-opacity duration-600 ease-in-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-600 ease-in-out"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div :key="currentSlide" class="absolute inset-0">
          <ImageWithFallback
            :src="carouselSlides[currentSlide].image"
            :alt="carouselSlides[currentSlide].tagline"
            class="w-full h-full object-cover"
          />
        </div>
      </Transition>
    </div>

    <!-- Progress Indicators -->
    <div class="flex gap-2 mt-8 justify-center">
      <button
        v-for="(_slide, index) in carouselSlides"
        :key="index"
        @click="currentSlide = index"
        class="relative h-1.5 w-12 bg-[#2A2018] rounded-full overflow-hidden"
        :aria-label="`Go to slide ${index + 1}`"
      >
        <Transition
          enter-active-class="transition-all duration-[5000ms] ease-linear"
          enter-from-class="w-0"
          enter-to-class="w-full"
        >
          <div
            v-if="index === currentSlide"
            :key="`progress-${currentSlide}`"
            class="absolute inset-0 bg-[#D4956A] rounded-full"
          />
        </Transition>
        <div v-if="index < currentSlide" class="absolute inset-0 bg-[#D4956A] rounded-full" />
      </button>
    </div>
  </div>
</template>
